import { Loader2, Check } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import type { Item, TagListConfig } from "./useTagCategoryList";

export const TagDeleteDialog = ({
  pendingDelete, deleting, cfg, onCancel, onConfirm,
}: {
  pendingDelete: Item | null;
  deleting: boolean;
  cfg: TagListConfig;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && onCancel()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {cfg.singular} "{pendingDelete?.name}"?</AlertDialogTitle>
        <AlertDialogDescription>
          {pendingDelete?.count
            ? `It will be removed from ${pendingDelete.count} ${pendingDelete.count === 1 ? "excerpt" : "excerpts"}. `
            : "It isn't used by any excerpts. "}
          This cannot be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={(e) => { e.preventDefault(); onConfirm(); }}
          disabled={deleting}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {deleting ? "Deleting…" : "Delete"}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const TagMergeDialog = ({
  pendingMerge, items, mergeTargetId, setMergeTargetId, merging, cfg, onCancel, onConfirm,
}: {
  pendingMerge: Item | null;
  items: Item[];
  mergeTargetId: string | null;
  setMergeTargetId: (id: string | null) => void;
  merging: boolean;
  cfg: TagListConfig;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <Dialog
    open={!!pendingMerge}
    onOpenChange={(open) => { if (!open) onCancel(); }}
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Merge "{pendingMerge?.name}" into…</DialogTitle>
      </DialogHeader>
      <Command>
        <CommandInput placeholder={`Search ${cfg.singular}s…`} />
        <CommandList>
          <CommandEmpty>No matches.</CommandEmpty>
          <CommandGroup>
            {items
              .filter((i) => i.id !== pendingMerge?.id)
              .map((i) => (
                <CommandItem
                  key={i.id}
                  value={i.name}
                  onSelect={() => setMergeTargetId(i.id)}
                  className={mergeTargetId === i.id ? "bg-accent" : ""}
                >
                  <span className="flex-1 truncate">{i.name}</span>
                  <Badge variant="secondary" className="ml-2 shrink-0">
                    {i.count}
                  </Badge>
                  {mergeTargetId === i.id && <Check className="ml-2 h-4 w-4" />}
                </CommandItem>
              ))}
          </CommandGroup>
        </CommandList>
      </Command>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={merging}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={merging || !mergeTargetId}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Merge"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
